import { Button, FormControl, Grid, TextField } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKSearchSelect from "components/GKSearchSelect";
import GKTextField from "components/GKTextField";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";

const GroupModal = ({ open, setOpen, refetch, groupData }) => {
  const groupId = groupData?.id;
  const [userData, setUserData] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ["GET_ALL_USERS"],
    queryFn: () => axiosInstance.get("/super_admin/advisors/"),
  });

  useEffect(() => {
    if (data?.data?.data) {
      setUserData(
        data.data.data.map((item) => ({
          name: `${item.name} - ${item.phone}` || "-",
          value: item.id,
        }))
      );
    }
  }, [data]);

  const { mutate: handleCreateGroup } = useMutation({
    mutationKey: ["CREATE_TEMPLATE"],
    mutationFn: (data) => axiosInstance.post("/advisor_group/", data),
    onSuccess: () => {
      refetch();
      setOpen(false); // Close the modal
    },
  });

  const { mutate: handleUpdateGroup } = useMutation({
    mutationKey: ["UPDATE_GROUP"],
    mutationFn: (data) =>
      axiosInstance.patch(`/advisor_group/${groupId}/`, data),
    onSuccess: () => {
      refetch();
      setOpen(false);
    },
  });

  const { mutate: handleDeleteGroup } = useMutation({
    mutationKey: ["DELETE_GROUP"],
    mutationFn: () => axiosInstance.delete(`/advisor_group/${groupId}/`),
    onSuccess: () => {
      refetch();
      setOpen(false);
    },
  });

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      title: groupId ? groupData?.title : "",
      users: groupId ? groupData.users.map((item) => item.id) : [],
    },
  });

  const onSubmit = (values) => {
    if (groupId) {
      handleUpdateGroup(values);
    } else {
      handleCreateGroup(values);
    }
  };

  useEffect(() => {
    if (groupId) {
      reset({
        title: groupData?.title || "",
        users: groupData?.users.map((item) => item.id) || [],
      });
    }
  }, [groupId, groupData, reset]);

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={groupId ? "Update Group" : "Create Group"}
      footer={
        <Grid container justifyContent={"flex-end"} gap={1}>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            type="submit"
            form="create-group-form"
            variant="outlined"
          >
            {groupId ? "Update" : "Save"}
          </Button>
        </Grid>
      }
      titleComponent={
        groupId && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDeleteGroup()}
          >
            Delete Group
          </Button>
        )
      }
    >
      <form id="create-group-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    inputLabel="Group Name"
                    variant="outlined"
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="users"
              control={control}
              render={({ field: { value, onChange } }) => (
                <GKSearchSelect
                  multiple
                  disableClearable={false}
                  disableCloseOnSelect
                  limitTags={5}
                  loading={isLoading}
                  options={userData}
                  getOptionLabel={(option) => option.name || ""}
                  value={userData.filter((option) =>
                    value.includes(option.value)
                  )}
                  onChange={(e, newValue) => {
                    onChange(newValue.map((item) => item.value));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputLabel="User"
                      placeholder="Select User"
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
};

export default GroupModal;
